import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { FaTimes, FaSave } from 'react-icons/fa';
import { 
  ManufacturerInterface,
  createManufacturer,
  updateManufacturer
} from '../../api/manufacturers';

interface ManufacturerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  manufacturer?: ManufacturerInterface | null;
  onSuccess: () => void;
}

export const ManufacturerFormModal: React.FC<ManufacturerFormModalProps> = ({ 
  isOpen, 
  onClose, 
  manufacturer,
  onSuccess 
}) => {
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    country: '',
    is_active: true
  });

  const authToken = keycloak.token;
  const isEditing = !!manufacturer;

  // Initialize form data when manufacturer changes
  useEffect(() => {
    if (manufacturer) {
      setFormData({
        name: manufacturer.name,
        description: manufacturer.description || '',
        website: manufacturer.website || '',
        contact_email: manufacturer.contact_email || '',
        contact_phone: manufacturer.contact_phone || '',
        address: manufacturer.address || '',
        country: manufacturer.country || '',
        is_active: manufacturer.is_active ?? true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        website: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        country: '',
        is_active: true
      });
    }
  }, [manufacturer]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🏭 Submitting manufacturer form...', { isEditing, formData });
    
    if (!authToken) {
      toast.error('Authentication required');
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error('Manufacturer name is required');
      return;
    }

    setLoading(true);
    try {
      if (isEditing && manufacturer) {
        console.log('🔄 Updating manufacturer:', manufacturer.id);
        await updateManufacturer(authToken, manufacturer.id, formData);
        toast.success('Manufacturer updated successfully');
      } else {
        console.log('➕ Creating new manufacturer...');
        const result = await createManufacturer(authToken, formData);
        console.log('✅ Manufacturer created:', result);
        toast.success('Manufacturer created successfully');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving manufacturer:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Handle specific errors
      if (errorMessage.includes('422')) {
        toast.error('Invalid manufacturer data. Please check all fields.');
      } else if (errorMessage.includes('409') || errorMessage.includes('conflict')) {
        toast.error('A manufacturer with this name already exists.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error('You do not have permission to manage manufacturers.');
      } else if (errorMessage.includes('500')) {
        toast.error('Server error occurred. Please try again in a few moments.');
      } else {
        toast.error(isEditing ? `Failed to update manufacturer: ${errorMessage}` : `Failed to create manufacturer: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
          <h2 className="text-xl font-semibold text-[#171717]">
            {isEditing ? 'Edit Manufacturer' : 'Add New Manufacturer'}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-[#aaa] hover:text-[#555] transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888]"
              placeholder="Manufacturer name"
              required
            />
          </div>

          {/* Country */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Country
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888]"
              placeholder="e.g. United States"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888]"
              placeholder="Brief description of the manufacturer"
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888]"
              placeholder="https://example.com"
            />
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Contact Email
            </label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => handleInputChange('contact_email', e.target.value)}
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888]"
              placeholder="contact@example.com"
            />
          </div>

          {/* Contact Phone */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Contact Phone
            </label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888]"
              placeholder="+1-555-0123"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0 focus:border-[#888]"
              placeholder="Full address of the manufacturer"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-[#555] border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              <FaSave className="w-4 h-4" />
              <span>{loading ? 'Saving...' : (isEditing ? 'Update' : 'Save')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 